import { isNil } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  type HelloSignSessionResponse,
  helloSignSessionResponseSchema,
} from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const createHelloSignSessionSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.helloSignSession),
    attributes: z
      .object({
        childRequirementId: z.string(),
      })
      .optional(),
  }),
});

type CreateHelloSignSession = z.infer<typeof createHelloSignSessionSchema>;

interface UseCreateHelloSignSessionProps {
  requirementId?: string;
  workerId?: string;
}

export function useCreateHelloSignSession(
  props: UseCreateHelloSignSessionProps,
  options: UseMutationOptions<HelloSignSessionResponse, AxiosError, CreateHelloSignSession> = {}
): UseMutationResult<HelloSignSessionResponse, AxiosError, CreateHelloSignSession> {
  return useMutation({
    mutationFn: async (variables: CreateHelloSignSession) => {
      const { requirementId, workerId } = props;

      if (isNil(workerId) || isNil(requirementId)) {
        throw new Error("Missing required parameters");
      }

      const response = await post({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/workers/${workerId}/requirements/${requirementId}/hello-sign-sessions`,
        data: variables,
        requestSchema: createHelloSignSessionSchema,
        responseSchema: helloSignSessionResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_HELLO_SIGN_SESSION_FAILURE,
    },
    ...options,
  });
}
